import { apiClient } from '@/api/client';
import { tokenStore } from '@/api/tokenStore';

export interface MfaStatus {
  available: boolean;
  totp: { enabled: boolean; confirmedAt: string | null };
  recoveryCodesRemaining: number;
}
export interface EnrollmentStart {
  /** Shown once to set up the authenticator; never stored by the portal. */
  secret: string;
  otpauthUri: string;
  expiresAt: string;
}
interface MaybeToken { accessToken?: string }

/** Responses that carry an upgraded access token (MFA proven in this session) replace the held one. */
const adopt = <T extends MaybeToken>(data: T): T => {
  if (data.accessToken) tokenStore.set(data.accessToken);
  return data;
};

/** Two-step verification (identity-service via the gateway). */
export const mfaApi = {
  async status(): Promise<MfaStatus> {
    return (await apiClient.get<MfaStatus>('/me/mfa')).data;
  },
  async start(currentPassword: string): Promise<EnrollmentStart> {
    return (await apiClient.post<EnrollmentStart>('/me/mfa/totp/start', { currentPassword })).data;
  },
  async confirm(code: string): Promise<{ recoveryCodes: string[] }> {
    return adopt((await apiClient.post<{ recoveryCodes: string[] } & MaybeToken>('/me/mfa/totp/confirm', { code })).data);
  },
  async regenerate(currentPassword: string, code: string): Promise<{ recoveryCodes: string[] }> {
    return (await apiClient.post<{ recoveryCodes: string[] }>('/me/mfa/recovery-codes', { currentPassword, code })).data;
  },
  async disable(currentPassword: string, factor: { code?: string; recoveryCode?: string }): Promise<void> {
    await apiClient.post('/me/mfa/disable', { currentPassword, ...factor });
  },
  async stepUp(code: string): Promise<{ verifiedFor: number }> {
    return adopt((await apiClient.post<{ verifiedFor: number } & MaybeToken>('/auth/step-up', { code })).data);
  },
};
